/**
 * Main Layout Component
 * მთავარი Layout კომპონენტი
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import { Sidebar } from './Sidebar';
import type { MenuItem, BreadcrumbItem, User } from '../../types';

export interface LayoutProps {
  children: React.ReactNode;
  sidebarItems: MenuItem[];
  logo?: React.ReactNode;
  logoCollapsed?: React.ReactNode;
  user?: User | null;
  onLogout?: () => void;
  headerContent?: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  sidebarFooter?: React.ReactNode;
  className?: string;
}

export function Layout({
  children,
  sidebarItems,
  logo,
  logoCollapsed,
  user,
  onLogout,
  headerContent,
  breadcrumbs,
  sidebarFooter,
  className,
}: LayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className={clsx('flex h-screen bg-gray-100', className)}>
      {/* Sidebar - Desktop */}
      <div className="hidden lg:flex">
        <Sidebar
          items={sidebarItems}
          logo={logo}
          logoCollapsed={logoCollapsed}
          collapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
          footer={sidebarFooter}
        />
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Mobile */}
      <div
        className={clsx(
          'fixed inset-y-0 left-0 z-50 lg:hidden transition-transform duration-300',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <Sidebar
          items={sidebarItems}
          logo={logo}
          logoCollapsed={logoCollapsed}
          collapsed={false}
          footer={sidebarFooter}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 h-16 flex items-center px-4 lg:px-6">
          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 mr-2"
            onClick={() => setMobileMenuOpen(true)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Breadcrumbs */}
          {breadcrumbs && breadcrumbs.length > 0 && (
            <nav className="hidden sm:flex items-center text-sm">
              {breadcrumbs.map((item, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <span className="mx-2 text-gray-400">/</span>}
                  {item.path ? (
                    <a href={item.path} className="text-gray-500 hover:text-gray-700">
                      {item.label}
                    </a>
                  ) : (
                    <span className="text-gray-900 font-medium">{item.label}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          )}

          {/* Custom header content */}
          <div className="flex-1">{headerContent}</div>

          {/* User menu */}
          {user && (
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-900">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium">
                  {(user.firstName?.[0] || user.email[0]).toUpperCase()}
                </div>
                {onLogout && (
                  <button
                    onClick={onLogout}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                    title="Logout"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

// Page Header Component
export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  className?: string;
}

export function PageHeader({ title, subtitle, action, className }: PageHeaderProps) {
  return (
    <div className={clsx('flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6', className)}>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}
